import { z } from 'zod'
import {
  ABONNEMENT_VERLENGINGSREGIMES,
  type AbonnementStatus,
  type AbonnementType,
  type AbonnementVerlengingsregime,
  type TelefoonKostensoortCode,
} from './abonnement'

/**
 * batch-62 telefoonabonnementen (FO-463) - het telefonie-register.
 *
 * GEEN apart `TelefoonAbonnement`-model (CC.0b): een telefoonnummer of data-only
 * SIM is een `Abonnement` met type TELEFOON of DATA. Het nummer (E.164) en de
 * ICCID staan als matchsleutels op het abonnement, de SIM/het toestel is het
 * gekoppelde `VoorraadUniekItem`, de historie van medewerker + toestel leeft in
 * `AbonnementKoppelPeriode` en de providerfactuur-boekingen in
 * `AbonnementFactuurRegel`. Eén register, geen tweede waarheid.
 *
 * Dit bestand is het gedeelde contract (API + backoffice) voor: het register-
 * overzicht (filters, telling, maandlasten per BV), de bulkacties, de
 * nummerportering, de providerlijst, de instellingen, de providerfactuur-
 * specificatie, de controle-werkvoorraad en het import-rapport.
 */

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet jjjj-mm-dd zijn')

// Bedragen als Decimal-string (de form normaliseert komma -> punt vóór versturen).
const geldBedrag = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Bedrag moet een geldig getal zijn (bv. 35.00)')

const reden = z
  .string()
  .trim()
  .min(3, 'Geef een reden (minimaal 3 tekens)')
  .max(2000, 'De reden is te lang (maximaal 2000 tekens)')

// ─── Types in het register ───────────────────────────────────────────────────

/** TELEFOON = nummer met spraak/data; DATA = data-only SIM (tablet, tracker, router). */
export const TELEFONIE_ABONNEMENT_TYPES = ['TELEFOON', 'DATA'] as const satisfies readonly AbonnementType[]
export type TelefonieAbonnementType = (typeof TELEFONIE_ABONNEMENT_TYPES)[number]

export const TELEFONIE_ABONNEMENT_TYPE_LABELS: Record<TelefonieAbonnementType, string> = {
  TELEFOON: 'Telefoon',
  DATA: 'Data-only (tablet, tracker)',
}

export function isTelefonieType(type: AbonnementType): type is TelefonieAbonnementType {
  return (TELEFONIE_ABONNEMENT_TYPES as readonly string[]).includes(type)
}

// ─── Register-status (afgeleid, geen eigen kolom) ────────────────────────────
//
// De spec-statussen IN_VOORRAAD/IN_GEBRUIK zijn geen contractstatus maar een
// gebruiksdimensie: het contract LOOPT (ACTIEF) en er is wel of geen huidige
// gebruiker. Afleiden i.p.v. opslaan houdt één waarheid (`telefoonRegisterStatus`
// in @streep/utils). GEBLOKKEERD is wél een contractstatus (ADD VALUE).

export const TELEFOON_REGISTER_STATUSSEN = [
  'IN_GEBRUIK',
  'IN_VOORRAAD',
  'GEBLOKKEERD',
  'OPGEZEGD',
  'VERLOPEN',
  'CONCEPT',
] as const
export type TelefoonRegisterStatus = (typeof TELEFOON_REGISTER_STATUSSEN)[number]

export const TELEFOON_REGISTER_STATUS_LABELS: Record<TelefoonRegisterStatus, string> = {
  IN_GEBRUIK: 'In gebruik',
  IN_VOORRAAD: 'In voorraad',
  GEBLOKKEERD: 'Geblokkeerd',
  OPGEZEGD: 'Opgezegd',
  VERLOPEN: 'Verlopen',
  CONCEPT: 'Concept',
}

export const TELEFOON_REGISTER_STATUS_UITLEG: Record<TelefoonRegisterStatus, string> = {
  IN_GEBRUIK: 'Het contract loopt en het nummer staat op een medewerker.',
  IN_VOORRAAD: 'Het contract loopt, maar het nummer is (tijdelijk) niet in gebruik. Facturen blijven op het abonnement boeken.',
  GEBLOKKEERD: 'De SIM is bij de provider geblokkeerd (bv. na verlies). Het contract loopt door.',
  OPGEZEGD: 'Het contract is opgezegd.',
  VERLOPEN: 'De einddatum is verstreken zonder verlenging.',
  CONCEPT: 'Nog niet in gebruik genomen.',
}

// Badge-varianten gelijk aan @streep/ui-fp Badge (die kent `danger`, geen `destructive`).
export const TELEFOON_REGISTER_STATUS_BADGE: Record<
  TelefoonRegisterStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'danger' | 'info'
> = {
  IN_GEBRUIK: 'success',
  IN_VOORRAAD: 'info',
  GEBLOKKEERD: 'warning',
  OPGEZEGD: 'secondary',
  VERLOPEN: 'danger',
  CONCEPT: 'secondary',
}

// ─── Providerfactuur: kostensoorten, schema's, statussen ─────────────────────

export const TELEFOON_KOSTENSOORTEN = [
  'ABONNEMENT',
  'VERBRUIK',
  'EENMALIG',
  'TOESTEL',
  'KORTING',
  'OVERIG',
] as const satisfies readonly TelefoonKostensoortCode[]
export type TelefoonKostensoort = (typeof TELEFOON_KOSTENSOORTEN)[number]

export const TELEFOON_KOSTENSOORT_LABELS: Record<TelefoonKostensoort, string> = {
  ABONNEMENT: 'Abonnementskosten',
  VERBRUIK: 'Verbruik buiten de bundel',
  EENMALIG: 'Eenmalige kosten',
  TOESTEL: 'Toestelkosten',
  KORTING: 'Korting',
  OVERIG: 'Overig',
}

/**
 * Kostensoorten die als MAANDLAST gelden in de afwijkingscontrole (FO-463 §2.10):
 * het gefactureerde maandbedrag per nummer vergeleken met het geregistreerde
 * abonnementsbedrag. Toestelkosten (afbetaling toestel) zijn een aparte stroom
 * en tellen daar bewust niet in mee; ze blijven wel zichtbaar en geboekt.
 */
export const TELEFOON_MAANDLAST_KOSTENSOORTEN: readonly TelefoonKostensoort[] = [
  'ABONNEMENT',
  'VERBRUIK',
  'EENMALIG',
  'KORTING',
  'OVERIG',
]

export const TELEFOON_FACTUUR_SCHEMAS = ['VODAFONE', 'ODIDO', 'GENERIEK'] as const
export type TelefoonFactuurSchema = (typeof TELEFOON_FACTUUR_SCHEMAS)[number]

export const TELEFOON_FACTUUR_SCHEMA_LABELS: Record<TelefoonFactuurSchema, string> = {
  VODAFONE: 'Vodafone (specificatie per aansluiting)',
  ODIDO: 'Odido (overzicht per nummer)',
  GENERIEK: 'Algemeen (per nummer)',
}

export const TELEFOON_SPECIFICATIE_STATUSSEN = ['TE_CONTROLEREN', 'GEBOEKT', 'FOUT'] as const
export type TelefoonSpecificatieStatus = (typeof TELEFOON_SPECIFICATIE_STATUSSEN)[number]

export const TELEFOON_SPECIFICATIE_STATUS_LABELS: Record<TelefoonSpecificatieStatus, string> = {
  TE_CONTROLEREN: 'Te controleren',
  GEBOEKT: 'Geboekt op de abonnementen',
  FOUT: 'Uitlezen mislukt',
}

export const TELEFOON_REGEL_MATCHES = ['NUMMER', 'ICCID', 'HANDMATIG', 'ONBEKEND', 'GENEGEERD'] as const
export type TelefoonRegelMatch = (typeof TELEFOON_REGEL_MATCHES)[number]

export const TELEFOON_REGEL_MATCH_LABELS: Record<TelefoonRegelMatch, string> = {
  NUMMER: 'Gekoppeld op nummer',
  ICCID: 'Gekoppeld op ICCID',
  HANDMATIG: 'Handmatig gekoppeld',
  ONBEKEND: 'Nummer niet in het register',
  GENEGEERD: 'Genegeerd',
}

// ─── Controle-werkvoorraad ───────────────────────────────────────────────────

export const TELEFOON_CONTROLE_TYPES = [
  'ONBEKEND_NUMMER',
  'ZONDER_FACTUURREGEL',
  'AFWIJKING',
  'IMPORT_CONFLICT',
  'IMPORT_MEDEWERKER_ONBEKEND',
] as const
export type TelefoonControleType = (typeof TELEFOON_CONTROLE_TYPES)[number]

export const TELEFOON_CONTROLE_TYPE_LABELS: Record<TelefoonControleType, string> = {
  ONBEKEND_NUMMER: 'Nummer niet in het register',
  ZONDER_FACTUURREGEL: 'Abonnement zonder factuurregel',
  AFWIJKING: 'Maandbedrag wijkt af',
  IMPORT_CONFLICT: 'Importconflict',
  IMPORT_MEDEWERKER_ONBEKEND: 'Gebruiker uit de import niet gevonden',
}

export const TELEFOON_CONTROLE_TYPE_UITLEG: Record<TelefoonControleType, string> = {
  ONBEKEND_NUMMER: 'Nummer niet in het register - toevoegen of koppelen.',
  ZONDER_FACTUURREGEL:
    'De provider factureerde deze maand, maar niet voor dit abonnement. Betalen we voor een nummer dat de provider niet factureert, of andersom?',
  AFWIJKING: 'Het gefactureerde maandbedrag wijkt af van het geregistreerde abonnementsbedrag (bv. buitenbundel).',
  IMPORT_CONFLICT: 'Het nummer staat al in het register met andere gegevens. Beoordeel welke waarde klopt.',
  IMPORT_MEDEWERKER_ONBEKEND: 'De import noemde een gebruiker die niet als medewerker bekend is. Koppel handmatig of laat het nummer in voorraad.',
}

export const TELEFOON_CONTROLE_STATUSSEN = ['OPEN', 'AFGEHANDELD'] as const
export type TelefoonControleStatus = (typeof TELEFOON_CONTROLE_STATUSSEN)[number]

export const TELEFOON_CONTROLE_AFHANDELINGEN = ['GEKOPPELD', 'TOEGEVOEGD', 'GENEGEERD', 'OPGELOST'] as const
export type TelefoonControleAfhandeling = (typeof TELEFOON_CONTROLE_AFHANDELINGEN)[number]

export const TELEFOON_CONTROLE_AFHANDELING_LABELS: Record<TelefoonControleAfhandeling, string> = {
  GEKOPPELD: 'Gekoppeld aan een abonnement',
  TOEGEVOEGD: 'Toegevoegd aan het register',
  GENEGEERD: 'Genegeerd',
  OPGELOST: 'Opgelost',
}

// ─── Contract-/looptijdsignalen ──────────────────────────────────────────────

export const TELEFONIE_CONTRACT_SIGNALEN = [
  'CONTRACT_LOOPT_AF',
  'OPZEGTERMIJN_NADERT',
  'STILZWIJGENDE_VERLENGING',
  'STILZWIJGEND_VERLENGD',
] as const
export type TelefonieContractSignaal = (typeof TELEFONIE_CONTRACT_SIGNALEN)[number]

export const TELEFONIE_CONTRACT_SIGNAAL_LABELS: Record<TelefonieContractSignaal, string> = {
  CONTRACT_LOOPT_AF: 'Contract loopt af',
  OPZEGTERMIJN_NADERT: 'Opzegtermijn nadert',
  STILZWIJGENDE_VERLENGING: 'Wordt stilzwijgend verlengd',
  STILZWIJGEND_VERLENGD: 'Stilzwijgend verlengd',
}

// ─── Instellingen ────────────────────────────────────────────────────────────

export const TELEFONIE_INSTELLING_DEFAULTS = {
  /** Signaal "contract loopt af" / "wordt stilzwijgend verlengd" binnen X dagen. */
  signaalDagenContractEinde: 60,
  /** Signaal "opzegtermijn nadert" binnen X dagen vóór de opzegdeadline. */
  signaalDagenOpzegDeadline: 30,
  /** Vanaf deze dag van de maand controleren we "abonnement zonder factuurregel". */
  controleDagNaMaand: 20,
} as const

export const TelefonieInstellingSchema = z.object({
  signaalDagenContractEinde: z
    .number()
    .int('Gebruik hele dagen')
    .min(1, 'Minimaal 1 dag')
    .max(365, 'Maximaal 365 dagen'),
  signaalDagenOpzegDeadline: z
    .number()
    .int('Gebruik hele dagen')
    .min(1, 'Minimaal 1 dag')
    .max(365, 'Maximaal 365 dagen'),
  controleDagNaMaand: z
    .number()
    .int('Gebruik een hele dag')
    .min(1, 'Minimaal dag 1')
    .max(28, 'Maximaal dag 28'),
})
export type TelefonieInstellingInput = z.infer<typeof TelefonieInstellingSchema>

export interface TelefonieInstellingDTO extends TelefonieInstellingInput {
  /** ISO-tijdstip van de laatste wijziging; `null` = nog de standaardwaarden. */
  updatedAt: string | null
}

// ─── Providerlijst (beheerbaar) ──────────────────────────────────────────────

export const TelefoonProviderSchema = z.object({
  naam: z.string().trim().min(1, 'Naam is verplicht').max(80, 'Naam is te lang'),
  /** De leverancier (relatie type LEVERANCIER) waarvan de facturen binnenkomen. */
  leverancierId: z.string().min(1).nullable().optional(),
  factuurSchema: z.enum(TELEFOON_FACTUUR_SCHEMAS),
  /** Andere schrijfwijzen in importbestanden ("Vodafone Libertel", "T-Mobile"). */
  aliassen: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
  actief: z.boolean().optional(),
  sortering: z.number().int().min(0).max(999).optional(),
})
export type TelefoonProviderInput = z.infer<typeof TelefoonProviderSchema>

export const UpdateTelefoonProviderSchema = TelefoonProviderSchema.partial()
export type UpdateTelefoonProviderInput = z.infer<typeof UpdateTelefoonProviderSchema>

export interface TelefoonProviderDTO {
  id: string
  naam: string
  leverancierId: string | null
  leverancierNaam: string | null
  factuurSchema: TelefoonFactuurSchema
  aliassen: string[]
  actief: boolean
  sortering: number
  /** Aantal lopende abonnementen bij deze provider (via de leverancier). */
  aantalAbonnementen: number
}

// ─── Register-overzicht ──────────────────────────────────────────────────────

/** Sentinel voor "zonder provider" / "zonder BV" in de filters. */
export const TELEFONIE_FILTER_GEEN = 'GEEN'

export const TELEFONIE_SORTEERVELDEN = ['nummer', 'einddatum', 'medewerker', 'status'] as const
export type TelefonieSorteerveld = (typeof TELEFONIE_SORTEERVELDEN)[number]

export const TelefonieListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  zoek: z.string().trim().max(100).optional(),
  type: z.enum(TELEFONIE_ABONNEMENT_TYPES).optional(),
  registerStatus: z.enum(TELEFOON_REGISTER_STATUSSEN).optional(),
  /** Provider = de leverancier op het abonnement; `GEEN` = nog geen provider. */
  leverancierId: z.string().min(1).optional(),
  /** BV (Entity-id); `GEEN` = nog geen BV. */
  entityId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  einddatumVan: dateOnly.optional(),
  einddatumTot: dateOnly.optional(),
  /** `1` = alleen abonnementen zonder ingevuld maandbedrag. */
  zonderBedrag: z.enum(['1']).optional(),
  sort: z.enum(TELEFONIE_SORTEERVELDEN).default('nummer'),
  dir: z.enum(['asc', 'desc']).default('asc'),
})
export type TelefonieListQuery = z.infer<typeof TelefonieListQuerySchema>

/** Het gekoppelde middel (toestel of SIM) zoals het register het toont. */
export interface TelefonieMiddelDTO {
  id: string
  /** TELEFOON / SIMKAART / … (artikel-categorie). */
  categorie: string
  artikelNaam: string
  label: string
  imei: string | null
  serienummer: string | null
  iccid: string | null
  /** Status van het unieke item (OP_VOORRAAD, UITGEGEVEN, …). */
  status: string
  houderEmployeeId: string | null
  houderNaam: string | null
}

export interface TelefonieRegisterRowDTO {
  id: string
  /** Abonnementsnaam/bundel. */
  naam: string
  type: TelefonieAbonnementType
  status: AbonnementStatus
  registerStatus: TelefoonRegisterStatus
  /** E.164 (`+31612345678`) of `null`. */
  nummer: string | null
  nummerWeergave: string | null
  /** ICCID op het abonnement, anders die van de gekoppelde SIM. */
  iccid: string | null
  /** FO-508 (spec 208) — klantnummer bij de provider. */
  providerKlantnummer: string | null
  leverancier: { id: string; name: string } | null
  entity: { id: string; naam: string } | null
  employee: { id: string; naam: string } | null
  /** Het gekoppelde toestel of de SIM (VoorraadUniekItem). */
  middel: TelefonieMiddelDTO | null
  /** Decimal-string of `null` (nog niet ingevuld). */
  maandbedragExclBtw: string | null
  ingangsdatum: string | null
  einddatum: string | null
  looptijdMaanden: number | null
  opzegtermijnDagen: number | null
  verlengingsregime: AbonnementVerlengingsregime | null
  /** Dagen tot einddatum (Europe/Amsterdam); negatief = verstreken. */
  dagenTotEinde: number | null
  /** Dagen tot de opzegdeadline (einddatum - opzegtermijn). */
  dagenTotOpzegtermijn: number | null
  /** IN_VOORRAAD: sinds wanneer het nummer geparkeerd staat (jjjj-mm-dd), anders `null`. */
  inVoorraadSinds: string | null
}

export interface TelefonieListMetaDTO {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export interface TelefonieBvMaandlastDTO {
  /** `null` = abonnementen zonder BV. */
  entityId: string | null
  entityNaam: string
  /** Aantal lopende abonnementen in deze BV. */
  aantal: number
  /** Som van de INGEVULDE maandbedragen excl. btw (Decimal-string). */
  maandlastExclBtw: string
  /** Aantal lopende abonnementen zonder maandbedrag. */
  aantalZonderBedrag: number
  /** True zolang er bedragen ontbreken: het totaal is dan onvolledig. */
  onvolledig: boolean
}

export interface TelefonieSamenvattingDTO {
  totaal: number
  perStatus: Record<TelefoonRegisterStatus, number>
  perBv: TelefonieBvMaandlastDTO[]
  /** Totaal ingevulde maandlast excl. btw over alle BV's (Decimal-string). */
  maandlastExclBtw: string
  aantalZonderBedrag: number
  onvolledig: boolean
}

// ─── Bulkacties ──────────────────────────────────────────────────────────────

const bulkIds = z
  .array(z.string().min(1))
  .min(1, 'Kies minstens één abonnement')
  .max(200, 'Maximaal 200 abonnementen per bulkactie')

export const TELEFONIE_BULK_ACTIES = ['VERLENGEN', 'OPZEGGEN', 'OVERZETTEN', 'NAAR_VOORRAAD'] as const
export type TelefonieBulkActie = (typeof TELEFONIE_BULK_ACTIES)[number]

export const TELEFONIE_BULK_ACTIE_LABELS: Record<TelefonieBulkActie, string> = {
  VERLENGEN: 'Verlengen',
  OPZEGGEN: 'Opzeggen',
  OVERZETTEN: 'Overzetten naar medewerker',
  NAAR_VOORRAAD: 'Terug naar voorraad',
}

export const TelefonieBulkSchema = z.discriminatedUnion('actie', [
  z.object({
    actie: z.literal('VERLENGEN'),
    ids: bulkIds,
    einddatum: dateOnly,
    toelichting: z.string().trim().max(2000).optional(),
  }),
  z.object({
    actie: z.literal('OPZEGGEN'),
    ids: bulkIds,
    /** De datum per wanneer het contract eindigt (wordt de einddatum). */
    opzegdatum: dateOnly,
    reden,
  }),
  z.object({
    actie: z.literal('OVERZETTEN'),
    ids: bulkIds,
    employeeId: z.string().min(1, 'Kies een medewerker'),
    perDatum: dateOnly.optional(),
    reden: z.string().trim().max(2000).optional(),
  }),
  z.object({
    actie: z.literal('NAAR_VOORRAAD'),
    ids: bulkIds,
    perDatum: dateOnly.optional(),
    reden: z.string().trim().max(2000).optional(),
  }),
])
export type TelefonieBulkInput = z.infer<typeof TelefonieBulkSchema>

export interface TelefonieBulkRijResultaatDTO {
  id: string
  ok: boolean
  /** Waarom een rij niet is verwerkt (of een waarschuwing bij ok). */
  melding: string | null
}

export interface TelefonieBulkResultaatDTO {
  actie: TelefonieBulkActie
  verwerkt: number
  mislukt: number
  resultaten: TelefonieBulkRijResultaatDTO[]
}

// ─── Nummerportering ─────────────────────────────────────────────────────────

/**
 * Nummerportering (FO-463 §5.1): zelfde nummer, nieuw contract bij een (andere)
 * provider. Het oude abonnement wordt per de porteringsdatum opgezegd, het nieuwe
 * neemt nummer, medewerker, toestel en BV over; de historie blijft aan het nummer
 * hangen (beide contracten delen de E.164-sleutel).
 */
export const TelefonieNummerporteringSchema = z.object({
  porteringsdatum: dateOnly,
  /** De nieuwe provider (leverancier). */
  nieuwLeverancierId: z.string().min(1, 'Kies de nieuwe provider'),
  /** Nieuwe bundel/abonnementsnaam (leeg = de oude naam). */
  naam: z.string().trim().max(200).optional(),
  maandbedragExclBtw: geldBedrag.nullable().optional(),
  looptijdMaanden: z.number().int().positive().nullable().optional(),
  einddatum: dateOnly.nullable().optional(),
  opzegtermijnDagen: z.number().int().nonnegative().nullable().optional(),
  verlengingsregime: z.enum(ABONNEMENT_VERLENGINGSREGIMES).nullable().optional(),
  /** Nieuwe SIM (ICCID) indien bekend. */
  iccid: z.string().trim().max(40).nullable().optional(),
  reden: z.string().trim().max(2000).optional(),
})
export type TelefonieNummerporteringInput = z.infer<typeof TelefonieNummerporteringSchema>

// ─── Providerfactuur: specificatie + acties ──────────────────────────────────

export const TelefoonUitlezenSchema = z.object({
  /** Opnieuw uitlezen, ook als er al een specificatie is (vervangt die). */
  forceer: z.boolean().optional(),
})
export type TelefoonUitlezenInput = z.infer<typeof TelefoonUitlezenSchema>

export const TelefoonVerschilAccepterenSchema = z.object({ reden })
export type TelefoonVerschilAccepterenInput = z.infer<typeof TelefoonVerschilAccepterenSchema>

export interface TelefoonFactuurRegelDTO {
  id: string
  positie: number
  nummer: string | null
  nummerWeergave: string | null
  /** Het nummer zoals het op de factuur stond. */
  nummerRaw: string | null
  iccid: string | null
  kostensoort: TelefoonKostensoort
  omschrijving: string
  bedragExclBtw: string
  boekMaand: string
  match: TelefoonRegelMatch
  abonnementId: string | null
  geboekt: boolean
}

/** Alle regels van één nummer op de factuur + de afwijkingscontrole. */
export interface TelefoonFactuurNummerGroepDTO {
  sleutel: string
  nummer: string | null
  nummerWeergave: string | null
  abonnement: {
    id: string
    naam: string
    employeeNaam: string | null
    maandbedragExclBtw: string | null
  } | null
  regels: TelefoonFactuurRegelDTO[]
  /** Som maandlast-soorten (excl. toestel), Decimal-string. */
  gefactureerdExclBtw: string
  /** Som toestelkosten, Decimal-string. */
  toestelExclBtw: string
  /** Gefactureerd - geregistreerd maandbedrag; `null` zolang het maandbedrag ontbreekt. */
  verschil: string | null
  bovenDrempel: boolean
}

export interface TelefoonFactuurSpecificatieDTO {
  id: string
  factuurId: string
  status: TelefoonSpecificatieStatus
  schema: TelefoonFactuurSchema
  /** NL-maand van de factuurdatum (jjjj-mm). */
  factuurMaand: string
  uitgelezenOp: string
  /** `null` = automatisch uitgelezen (cron). */
  uitgelezenDoorNaam: string | null
  geboektOp: string | null
  somRegelsExclBtw: string
  btwBedrag: string
  factuurExclBtw: string
  factuurTotaal: string
  /** (som regels + btw) - factuurtotaal; 0.00 = sluit. */
  verschilBedrag: string
  sluit: boolean
  verschilGeaccepteerd: { doorNaam: string | null; op: string; reden: string } | null
  aantalRegels: number
  aantalGematcht: number
  aantalOnbekend: number
  markeringen: string[]
  foutmelding: string | null
  groepen: TelefoonFactuurNummerGroepDTO[]
}

export interface TelefoonFactuurPaneelDTO {
  /** Is de leverancier van deze inkoopfactuur een telefoonprovider uit de lijst? */
  isProviderFactuur: boolean
  provider: { id: string; naam: string; factuurSchema: TelefoonFactuurSchema } | null
  specificatie: TelefoonFactuurSpecificatieDTO | null
  /** Open controle-items van deze factuur (onbekende nummers, afwijkingen). */
  openControles: TelefoonControleItemDTO[]
  /** DEFINITIEVE regel-toewijzingen (FO-147) die de specificatie buiten de kosten houdt. */
  aantalRegelToewijzingen: number
}

// ─── Controle-items ──────────────────────────────────────────────────────────

export const TelefoonControleQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  type: z.enum(TELEFOON_CONTROLE_TYPES).optional(),
  status: z.enum(TELEFOON_CONTROLE_STATUSSEN).default('OPEN'),
  factuurId: z.string().min(1).optional(),
})
export type TelefoonControleQuery = z.infer<typeof TelefoonControleQuerySchema>

export interface TelefoonControleItemDTO {
  id: string
  type: TelefoonControleType
  status: TelefoonControleStatus
  omschrijving: string
  nummer: string | null
  nummerWeergave: string | null
  maand: string | null
  /** Decimal-strings (gevoelige data: alleen in backoffice/inkoop). */
  verschilBedrag: string | null
  gefactureerdBedrag: string | null
  afgesprokenBedrag: string | null
  abonnement: { id: string; naam: string; nummerWeergave: string | null } | null
  factuur: { id: string; factuurNr: string; leverancierNaam: string | null } | null
  /** Extra context (bv. de conflicterende importwaarden). */
  details: Record<string, string | null> | null
  createdAt: string
  afgehandeldOp: string | null
  afgehandeldDoorNaam: string | null
  afhandeling: TelefoonControleAfhandeling | null
  afhandelReden: string | null
}

export const TelefoonControleKoppelSchema = z.object({
  abonnementId: z.string().min(1, 'Kies een abonnement'),
})
export type TelefoonControleKoppelInput = z.infer<typeof TelefoonControleKoppelSchema>

export const TelefoonControleToevoegenSchema = z.object({
  type: z.enum(TELEFONIE_ABONNEMENT_TYPES).default('TELEFOON'),
  naam: z.string().trim().max(200).optional(),
  employeeId: z.string().min(1).nullable().optional(),
  entityId: z.string().min(1).nullable().optional(),
  maandbedragExclBtw: geldBedrag.nullable().optional(),
})
export type TelefoonControleToevoegenInput = z.infer<typeof TelefoonControleToevoegenSchema>

export const TelefoonControleAfhandelSchema = z.object({
  afhandeling: z.enum(['GENEGEERD', 'OPGELOST']),
  reden,
})
export type TelefoonControleAfhandelInput = z.infer<typeof TelefoonControleAfhandelSchema>

// ─── Werkvoorraad "contracten die aflopen" ───────────────────────────────────

export interface TelefonieContractSignaalRowDTO {
  abonnementId: string
  naam: string
  nummerWeergave: string | null
  employeeNaam: string | null
  leverancierNaam: string | null
  soort: TelefonieContractSignaal
  einddatum: string | null
  opzegDeadline: string | null
  /** Dagen tot het relevante moment (einde of opzegdeadline); 0 = vandaag. */
  dagen: number
  verlengingsregime: AbonnementVerlengingsregime | null
  /** Bij STILZWIJGEND_VERLENGD: wanneer de cron verlengde. */
  verlengdOp: string | null
}

export interface TelefonieWerkvoorraadDTO {
  instelling: TelefonieInstellingInput
  /** Alle lopende contracten met een signaal (uitputtend, geen plafond). */
  contracten: TelefonieContractSignaalRowDTO[]
  openControlesPerType: Record<TelefoonControleType, number>
}

// ─── Historie op het abonnement-detail ───────────────────────────────────────

export interface TelefonieKoppelPeriodeDTO {
  id: string
  startDatum: string
  eindDatum: string | null
  employee: { id: string; naam: string } | null
  middel: { id: string; label: string } | null
  reden: string | null
}

export interface TelefonieMaandFactuurDTO {
  /** jjjj-mm */
  maand: string
  /** Per kostensoort (Decimal-strings), alleen soorten met regels. */
  perSoort: Partial<Record<TelefoonKostensoort, string>>
  totaalExclBtw: string
  /** Maandlast (excl. toestel) - de basis van de afwijkingscontrole. */
  maandlastExclBtw: string
  /** Maandlast - geregistreerd maandbedrag; `null` zolang dat ontbreekt. */
  verschil: string | null
  bovenDrempel: boolean
  facturen: { id: string; factuurNr: string }[]
}

export interface TelefonieNummerContractDTO {
  id: string
  naam: string
  status: AbonnementStatus
  leverancierNaam: string | null
  ingangsdatum: string | null
  einddatum: string | null
  isHuidig: boolean
}

export interface TelefonieHistorieDTO {
  /** Of de viewer bedragen mag zien (canViewCostPrices; server-bepaald). */
  toonBedragen: boolean
  registerStatus: TelefoonRegisterStatus
  inVoorraadSinds: string | null
  /** De BV waar het nummer onder valt; `null` = nog geen BV. */
  entity: { id: string; naam: string } | null
  koppelPeriodes: TelefonieKoppelPeriodeDTO[]
  /** Alle contracten op hetzelfde nummer (portering): historie blijft aan het nummer. */
  nummerContracten: TelefonieNummerContractDTO[]
  /** Geboekte providerfacturen per maand (leeg zonder `toonBedragen`). */
  maanden: TelefonieMaandFactuurDTO[]
}

// ─── Import-rapport (dry-run) ────────────────────────────────────────────────

export const TELEFOON_IMPORT_UITKOMSTEN = [
  'NIEUW',
  'AANVULLEN',
  'ONGEWIJZIGD',
  'CONFLICT',
  'DUBBEL_IN_BESTAND',
  'ONGELDIG',
] as const
export type TelefoonImportUitkomst = (typeof TELEFOON_IMPORT_UITKOMSTEN)[number]

export const TELEFOON_IMPORT_UITKOMST_LABELS: Record<TelefoonImportUitkomst, string> = {
  NIEUW: 'Nieuw in het register',
  AANVULLEN: 'Bestaand: lege velden aanvullen',
  ONGEWIJZIGD: 'Bestaand: niets te wijzigen',
  CONFLICT: 'Conflict: naar review',
  DUBBEL_IN_BESTAND: 'Dubbel nummer in het bestand: naar review',
  ONGELDIG: 'Ongeldig: overgeslagen',
}

export type TelefoonImportRegisterStatus = 'IN_GEBRUIK' | 'IN_VOORRAAD' | 'OPGEZEGD' | 'GEBLOKKEERD'

export interface TelefoonImportRijRapportDTO {
  /** 1-based rijnummer in het bestand (koprij niet meegeteld). */
  rij: number
  nummerRaw: string | null
  nummer: string | null
  nummerWeergave: string | null
  uitkomst: TelefoonImportUitkomst
  registerStatus: TelefoonImportRegisterStatus
  medewerker: { gevonden: boolean; naam: string | null; bron: 'IMPORT' | 'TOESTEL' | null }
  middel: { gevonden: boolean; label: string | null }
  provider: { gevonden: boolean; naam: string | null }
  bv: { gevonden: boolean; naam: string | null }
  meldingen: string[]
  conflicten: { veld: string; bestaand: string | null; import: string | null }[]
}

export interface TelefoonImportRapportDTO {
  totaal: number
  perUitkomst: Record<TelefoonImportUitkomst, number>
  inGebruik: number
  inVoorraad: number
  medewerkerOnbekend: number
  zonderProvider: number
  zonderBedrag: number
  rijen: TelefoonImportRijRapportDTO[]
}
